import * as THREE from 'three';

import {
  DEFAULT_HANDLING,
  step as physicsStep,
  speed as racerSpeed,
  type InputState,
  type RacerState,
} from './Physics';
import {
  isInsideWalls,
  isOnTrack,
  locateTrackPosition,
  type Track,
} from '../track/TrackLoader';

export type SimState = RacerState;

export interface SimStepResult {
  state: SimState;
  wallMargin: number;
  trackMargin: number;
  onTrack: boolean;
  insideWalls: boolean;
  arcLength: number;
  speed: number;
}

export function fromRacer(state: RacerState): SimState {
  return {
    position: state.position.clone(),
    velocity: state.velocity.clone(),
    heading: state.heading,
    yawVelocity: state.yawVelocity,
    driftCharge: state.driftCharge,
    boostTimer: state.boostTimer,
    isDrifting: state.isDrifting,
    verticalVelocity: state.verticalVelocity,
    airborne: state.airborne,
  };
}

export function simStep(track: Track, state: SimState, input: InputState, dt: number): SimStepResult {
  const next = fromRacer(state);
  const racingSurface = isOnTrack(track, next.position.x, next.position.z);
  physicsStep(next, input, DEFAULT_HANDLING, racingSurface, dt);
  next.position.y = state.position.y;
  next.verticalVelocity = state.verticalVelocity;
  next.airborne = state.airborne;
  return describeSimState(track, next);
}

export function describeSimState(track: Track, state: SimState): SimStepResult {
  const pos = locateTrackPosition(track, state.position.x, state.position.z);
  const wall = wallMarginAt(track, state.position.x, state.position.z);
  const trackLimit = trackMarginAt(track, state.position.x, state.position.z);
  return {
    state,
    wallMargin: wall,
    trackMargin: trackLimit,
    onTrack: trackLimit >= -1e-6,
    insideWalls: wall >= -1e-6 && isInsideWalls(track, state.position.x, state.position.z),
    arcLength: pos.arcLength,
    speed: racerSpeed(state),
  };
}

export function wallMarginAt(track: Track, x: number, z: number): number {
  const pos = locateTrackPosition(track, x, z);
  return lateralMargin(pos.signedLateral, pos.widthLeft + track.wallBuffer, pos.widthRight + track.wallBuffer);
}

export function trackMarginAt(track: Track, x: number, z: number): number {
  const pos = locateTrackPosition(track, x, z);
  return lateralMargin(pos.signedLateral, pos.widthLeft, pos.widthRight);
}

export function cloneInput(input: InputState): InputState {
  return {
    throttle: input.throttle,
    brake: input.brake,
    steer: input.steer,
    leftThrust: input.leftThrust,
    rightThrust: input.rightThrust,
    boost: input.boost,
  };
}

export function velocityState(x: number, z: number, heading: number, speed: number): RacerState {
  return {
    position: new THREE.Vector3(x, 0, z),
    velocity: new THREE.Vector3(Math.sin(heading) * speed, 0, Math.cos(heading) * speed),
    heading,
    yawVelocity: 0,
    driftCharge: 0,
    boostTimer: 0,
    isDrifting: false,
    verticalVelocity: 0,
    airborne: false,
  };
}

function lateralMargin(signedLateral: number, widthLeft: number, widthRight: number): number {
  return signedLateral >= 0
    ? widthLeft - signedLateral
    : widthRight + signedLateral;
}
